import { createContext } from "react";
import {
  Ability,
  AbilityClass,
  AbilityBuilder,
  BuilderCanParameters,
} from "@casl/ability";

import {
  ROLE_USER,
  ROLE_APPLICATION_DEV_ADMIN_PORTAL_TECH,
  ROLE_MERCHANT_PORTAL_ADMIN_TECH,
  ROLE_MERCHANT_PORTAL_ADMIN_FINANCE,
  ROLE_PSP_PORTAL_ADMIN_TECH,
  ROLE_PSP_PORTAL_ADMIN_FINANCE,
  ROLE_PSP_PORTAL,
  ROLE_PSP_MERCHANT_PORTAL,
  ROLE_VOLT_PORTAL_ADMIN,
  ROLE_VOLT_PORTAL_SUPPORT,
  ROLE_MERCHANT_PORTAL,
  ROLE_VOLT_PORTAL_STANDARD,
} from "../config/roles";
import { Subject } from "../models/role/subject";
import { Action } from "../models/role/action";
import Role from "../models/Role";

export type AppAbilityType = Ability<[Action, Subject]>;
export const AppAbility = Ability as AbilityClass<AppAbilityType>;

const ACTIONS_READ: Action[] = ["details", "list"];
const ACTIONS_WRITE: Action[] = ["create", "update"];
const ACTIONS_CRUD: Action[] = [...ACTIONS_READ, ...ACTIONS_WRITE];

interface AbilityOptions {
  roles?: Role[];
}

interface AbilityInterface {
  can: (...args: BuilderCanParameters<AppAbilityType>) => any;
  cannot: (...args: BuilderCanParameters<AppAbilityType>) => any;
}

const definePermissionsFor = ({ roles }: AbilityOptions) => {
  const { can, cannot, rules } = new AbilityBuilder<AppAbilityType>(AppAbility);

  if (roles?.includes(ROLE_USER)) {
    UserAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_APPLICATION_DEV_ADMIN_PORTAL_TECH)) {
    ApplicationDevAdminTechAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_MERCHANT_PORTAL_ADMIN_TECH)) {
    MerchantAdminTechAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_MERCHANT_PORTAL_ADMIN_FINANCE)) {
    MerchantAdminFinanceAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_PSP_PORTAL_ADMIN_TECH)) {
    PSPAdminTechAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_PSP_PORTAL_ADMIN_FINANCE)) {
    PSPAdminFinanceAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_PSP_PORTAL)) {
    PSPAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_PSP_MERCHANT_PORTAL)) {
    PSPMerchantAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_VOLT_PORTAL_ADMIN)) {
    VoltAdmin({ can, cannot });
  }

  if (roles?.includes(ROLE_VOLT_PORTAL_SUPPORT)) {
    VoltSupportAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_MERCHANT_PORTAL)) {
    MerchantAbility({ can, cannot });
  }

  if (roles?.includes(ROLE_VOLT_PORTAL_STANDARD)) {
    VoltStandardAbility({ can, cannot });
  }

  // Every role permissions
  can(["list"], ["currency", "transfer-type", "country"]);
  can(["list", "details"], ["cash-management"]);
  can(ACTIONS_READ, ["payment", "customer-address"]);

  return rules;
};

function UserAbility(ability: AbilityInterface) {}

function ApplicationDevAdminTechAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, ["account", "bank"]);
  ability.can(ACTIONS_CRUD, ["client"]);
  ability.can(["test", "undelivered", "failure"], "self-notification");
  ability.can(
    ["update", ...ACTIONS_READ],
    ["self-customer", "self-customer-preferences"]
  );
}

function MerchantAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, [
    "account",
    "bank",
    "client",
    "children-customer",
    "children-customer-preferences",
    "payment",
  ]);
  ability.can(
    ["failure", "undelivered"],
    ["self-notification", "children-notification"]
  );
}

function MerchantAdminTechAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_CRUD, [
    "client",
    "self-cash-management-settlement-notification",
    "children-cash-management-settlement-notification",
  ]);
  ability.can(ACTIONS_READ, "account");
  ability.can(
    ["test", "undelivered", "failure"],
    ["self-notification", "children-notification"]
  );
  ability.can(ACTIONS_READ, "payment");
  ability.can(
    [...ACTIONS_READ, "update"],
    ["self-customer", "self-customer-preferences"]
  );
  ability.can(
    [...ACTIONS_READ, "update"],
    ["children-customer", "children-customer-preferences"]
  );
}

function MerchantAdminFinanceAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, "account");
  ability.can(ACTIONS_READ, ["bank", "client"]);
  ability.can(["undelivered"], ["self-notification", "children-notification"]);
  ability.can(ACTIONS_READ, "payment");
  ability.can([...ACTIONS_READ, "update"], "self-customer");
  ability.can([...ACTIONS_READ, "update"], "children-customer");
  ability.can(ACTIONS_READ, "self-customer-preferences");
  ability.can(ACTIONS_READ, "children-customer-preferences");
  ability.can(ACTIONS_CRUD, [
    "self-cash-management-settlement-notification",
    "children-cash-management-settlement-notification",
  ]);
}

function PSPAdminTechAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, ["account", "bank", "payment"]);
  ability.can(ACTIONS_CRUD, "client");
  ability.can(
    [...ACTIONS_READ, "update"],
    ["self-customer", "self-customer-preferences"]
  );
  ability.can(
    [...ACTIONS_READ, "update"],
    ["children-customer", "children-customer-preferences"]
  );
  ability.can(
    ["test", "undelivered", "failure"],
    ["self-notification", "children-notification"]
  );

  ability.can(["create"], ["merchant", "division"]);
  ability.can(ACTIONS_CRUD, [
    "self-cash-management-settlement-notification",
    "children-cash-management-settlement-notification",
  ]);
}

function PSPAdminFinanceAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, "account");
  ability.can(ACTIONS_READ, ["bank", "client"]);
  ability.can(["undelivered"], ["children-notification", "self-notification"]);
  ability.can(ACTIONS_READ, [
    "children-customer",
    "children-customer-preferences",
    "self-customer-preferences",
  ]);
  ability.can(ACTIONS_READ, ["payment"]);
  ability.can([...ACTIONS_READ, "update"], "self-customer");
  ability.can(ACTIONS_CRUD, [
    "self-cash-management-settlement-notification",
    "children-cash-management-settlement-notification",
  ]);
}

function PSPAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, ["bank", "client", "payment", "account"]);
  ability.can(["undelivered"], ["self-notification", "children-notification"]);
  ability.can(ACTIONS_READ, [
    "children-customer",
    "children-customer-preferences",
    "self-customer-preferences",
  ]);
  ability.can([...ACTIONS_READ, "update"], "self-customer");
}
function PSPMerchantAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, [
    "bank",
    "children-customer",
    "children-customer-preferences",
    "self-customer-preferences",
    "account",
  ]);
  ability.can([...ACTIONS_READ, "update"], "self-customer");
  ability.can(ACTIONS_READ, ["payment"]);
}

function VoltAdmin(ability: AbilityInterface) {
  ability.can(ACTIONS_CRUD, [
    "account",
    "bank",
    "country",
    "currency",
    "self-customer",
    "children-customer",
    "customer-preferences",
    "self-customer-preferences",
    "children-customer-preferences",
    "provider",
    "institution",
    "customer-address",
    "payment-extended",
    "cash-management-provider-setup",
    "customer-banks",
    "cash-management-settlement-notification",
    "customer-payments-providers",
  ]);
  ability.can(
    ["test", "failure", "undelivered"],
    ["children-notification", "self-notification"]
  );
  ability.can([...ACTIONS_READ, "update"], "payment");
  ability.can(ACTIONS_CRUD, "transfer-type");
  ability.can(ACTIONS_CRUD, "user");
  ability.can(ACTIONS_CRUD, "cash-management");
  ability.can(ACTIONS_READ, "payer");

  ability.can([...ACTIONS_CRUD, "delete"], "client");

  ability.can([...ACTIONS_CRUD, "impersonate"], "customer");
}

function VoltSupportAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, ["account", "bank", "payment-extended"]);
  ability.can(ACTIONS_CRUD, [
    "client",
    "customer-banks",
    "cash-management-settlement-notification",
  ]);

  ability.can(
    ["test", "failure", "undelivered"],
    ["children-notification", "self-notification"]
  );
  ability.can(ACTIONS_READ, [
    "customer",
    "customer-preferences",
    "payment",
    "provider",
    "user",
    "payer",
  ]);
}

function VoltStandardAbility(ability: AbilityInterface) {
  ability.can(ACTIONS_READ, ["account", "bank", "client", "payment-extended"]);
  ability.can(
    ["failure", "undelivered"],
    ["children-notification", "self-notification"]
  );

  ability.can(ACTIONS_READ, [
    "customer",
    "customer-preferences",
    "payment",
    "provider",
    "user",
    "payer",
  ]);
}

export function defineAbilityFor({ roles }: { roles?: Role[] }) {
  return new AppAbility(definePermissionsFor({ roles: roles }));
}

const defaultAbility = defineAbilityFor({});
export const AbilityContext = createContext(defaultAbility);
